import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Clock,
  CheckCircle2,
  ShieldCheck,
  Building2,
  MapPin,
  ArrowRight,
  Zap,
  Phone,
  Mail,
  User as UserIcon,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const WHATSAPP_NUMBER = "919999999999"; // placeholder
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi! I need GST registration in Mumbai. Please help."
)}`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" },
  }),
};

function Header() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#064E3B]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-4 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#064E3B] flex items-center justify-center text-white">
            <Sparkles size={18} />
          </div>
          <span className="font-display text-xl font-bold text-[#022C22] tracking-tight">
            GST24 Mumbai
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#3F6255]">
          <a href="#how" className="hover:text-[#064E3B]">How it works</a>
          <a href="#pricing" className="hover:text-[#064E3B]">Pricing</a>
          <a href="#vpob" className="hover:text-[#064E3B]">VPOB / APOB</a>
          <a href="#faq" className="hover:text-[#064E3B]">FAQ</a>
        </nav>
        <a
          href="#lead"
          data-testid="header-cta"
          className="btn-primary text-sm hidden sm:inline-flex"
        >
          Get started
          <ArrowRight size={16} />
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-grain pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="inline-flex items-center gap-2 bg-[#F4F1EA] text-[#064E3B] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em]"
            data-testid="hero-badge"
          >
            <Clock size={14} /> 24-Hour GSTIN promise
          </motion.div>
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#022C22] mt-6 tracking-tighter leading-[1.05]"
          >
            Mumbai GST Registration{" "}
            <span className="text-[#064E3B]">in 24 Hours.</span>
            <br />
            <span className="text-[#0EA5E9]">VPOB &amp; APOB</span> done right.
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="text-lg text-[#3F6255] mt-6 max-w-xl leading-relaxed"
          >
            Fast, legal and hassle-free Virtual &amp; Alternate Place of Business
            for Amazon, Flipkart and Meesho sellers expanding into Maharashtra.
          </motion.p>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href="#lead" data-testid="hero-primary-cta" className="btn-primary">
              <Zap size={18} /> Get free consultation
            </a>
            <a href="#pricing" data-testid="hero-secondary-cta" className="btn-secondary">
              See pricing
            </a>
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
          >
            {[
              { k: "1,200+", v: "GSTINs issued" },
              { k: "24h", v: "Avg. turnaround" },
              { k: "₹1,499", v: "Starts at" },
              { k: "Mumbai", v: "Local address" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-2xl font-bold text-[#064E3B]">
                  {s.k}
                </div>
                <div className="text-xs text-[#64748B] uppercase tracking-widest mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={2}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-[#064E3B]/10 shadow-xl">
            <img
              src="https://images.pexels.com/photos/36330752/pexels-photo-36330752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Indian business owner smiling"
              className="w-full h-[480px] object-cover"
            />
            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-[#064E3B]/10">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#10B981]" size={22} />
                <div>
                  <div className="font-display font-bold text-[#022C22]">
                    Approved on Amazon &amp; Flipkart
                  </div>
                  <div className="text-xs text-[#64748B]">
                    Maharashtra-compliant VPOB &amp; APOB documents
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBadges() {
  const items = [
    { icon: ShieldCheck, label: "Maharashtra-compliant" },
    { icon: Clock, label: "24-hour processing" },
    { icon: Building2, label: "Real Mumbai address" },
    { icon: CheckCircle2, label: "100% paperwork handled" },
  ];
  return (
    <section className="border-y border-[#064E3B]/10 bg-[#F4F1EA]/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map(({ icon: Icon, label }) => (
          <div
            key={label}
            className="flex items-center gap-3 text-[#064E3B] text-sm font-medium"
          >
            <Icon size={18} className="text-[#064E3B]" />
            {label}
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Share your details", d: "Tell us about your business — 2-minute form." },
    { n: "02", t: "Upload documents", d: "PAN, Aadhaar, photo. We send you a checklist." },
    { n: "03", t: "We file your GST", d: "Our team files & follows up directly with GSTN." },
    { n: "04", t: "Get GSTIN in 24h", d: "GSTIN delivered to your email, ready to sell." },
  ];
  return (
    <section id="how" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9]">
            How it works
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#022C22] mt-3 tracking-tight">
            Four steps. Zero confusion.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              className="bg-white rounded-2xl p-6 border border-[#E5E7EB] hover:shadow-lg transition-shadow"
            >
              <div className="font-display text-4xl font-black text-[#064E3B]/15">
                {s.n}
              </div>
              <div className="font-display text-xl font-bold text-[#022C22] mt-3">
                {s.t}
              </div>
              <div className="text-sm text-[#3F6255] mt-2 leading-relaxed">{s.d}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ plans, onBuy, buyingId }) {
  return (
    <section id="pricing" className="py-24 md:py-32 bg-[#F4F1EA]/50 border-y border-[#064E3B]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9]">
            Pricing
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#022C22] mt-3 tracking-tight">
            Simple, all-inclusive plans.
          </h2>
          <p className="text-[#3F6255] mt-4 text-lg">
            No hidden charges. Government fees included where applicable.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((p) => {
            const highlight = p.id === "bundle_gst_vpob";
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={
                  highlight
                    ? "bg-[#064E3B] text-white border border-[#064E3B] rounded-2xl p-8 relative overflow-hidden"
                    : "bg-white border border-[#E5E7EB] rounded-2xl p-8 relative overflow-hidden hover:shadow-lg transition-shadow"
                }
              >
                {highlight && (
                  <div className="absolute top-4 right-4 bg-[#F59E0B] text-[#022C22] text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    Best value
                  </div>
                )}
                <div
                  className={`text-xs uppercase tracking-widest font-bold ${
                    highlight ? "text-[#A7F3D0]" : "text-[#0EA5E9]"
                  }`}
                >
                  {p.id === "gst_24h"
                    ? "Fast track"
                    : p.id === "vpob_mumbai"
                    ? "Virtual"
                    : p.id === "apob_mumbai"
                    ? "Add-on"
                    : "Bundle"}
                </div>
                <div
                  className={`font-display text-2xl font-bold mt-3 ${
                    highlight ? "text-white" : "text-[#022C22]"
                  }`}
                >
                  {p.name}
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span
                    className={`font-display text-4xl font-bold ${
                      highlight ? "text-white" : "text-[#022C22]"
                    }`}
                  >
                    ₹{p.price.toLocaleString("en-IN")}
                  </span>
                  {(p.id === "vpob_mumbai" || p.id === "apob_mumbai") && (
                    <span
                      className={`text-sm ${
                        highlight ? "text-[#A7F3D0]" : "text-[#64748B]"
                      }`}
                    >
                      /year
                    </span>
                  )}
                </div>
                <p
                  className={`mt-3 text-sm leading-relaxed ${
                    highlight ? "text-[#D1FAE5]" : "text-[#3F6255]"
                  }`}
                >
                  {p.description}
                </p>
                <button
                  type="button"
                  data-testid={`buy-${p.id}`}
                  onClick={() => onBuy(p.id)}
                  disabled={buyingId === p.id}
                  className={
                    highlight
                      ? "mt-6 w-full bg-white text-[#064E3B] rounded-full px-6 py-3 font-medium hover:bg-[#F4F1EA] transition-colors disabled:opacity-60"
                      : "mt-6 w-full bg-[#064E3B] text-white rounded-full px-6 py-3 font-medium hover:bg-[#042F2E] transition-colors disabled:opacity-60"
                  }
                >
                  {buyingId === p.id ? "Redirecting…" : "Buy Now"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function VpobExplainer() {
  return (
    <section id="vpob" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative">
          <div className="rounded-3xl overflow-hidden border border-[#064E3B]/10">
            <img
              src="https://images.pexels.com/photos/37268140/pexels-photo-37268140.jpeg"
              alt="Mumbai skyline"
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 border border-[#064E3B]/10 shadow-lg max-w-xs hidden md:block">
            <MapPin className="text-[#0EA5E9]" />
            <div className="font-display font-bold text-[#022C22] mt-2">
              Andheri (E), Mumbai
            </div>
            <div className="text-xs text-[#64748B] mt-1">
              Premium commercial address with signage &amp; receptionist.
            </div>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9]">
            What is VPOB &amp; APOB?
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#022C22] mt-3 tracking-tight">
            Sell in Maharashtra without renting an office.
          </h2>
          <p className="text-[#3F6255] mt-4 text-lg leading-relaxed">
            To list on Amazon, Flipkart or Meesho in Maharashtra you need a GSTIN
            tied to a local address. We provide a verifiable, GST-compliant
            <span className="font-semibold text-[#064E3B]"> Virtual Place of Business</span> (VPOB)
            in Mumbai — and{" "}
            <span className="font-semibold text-[#064E3B]">Additional Place of Business</span> (APOB)
            for sellers who already have a GSTIN.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              "NOC, rent agreement & utility bill — fully ready",
              "Signage, mail handling & GST officer-friendly",
              "Compliant for Amazon FBA / Flipkart Smart fulfillment",
              "Renewable annually, transparent paperwork",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle2 className="text-[#10B981] mt-0.5" size={18} />
                <span className="text-[#022C22]">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function LeadForm({ plans }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_interested: "gst_24h",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Thanks! Our team will contact you within 60 minutes.");
      setForm({ name: "", phone: "", email: "", service_interested: "gst_24h", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead" className="py-24 md:py-32 bg-[#064E3B] text-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#A7F3D0]">
            Free consultation
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 tracking-tight">
            Talk to a GST expert today.
          </h2>
          <p className="text-[#D1FAE5] mt-4 leading-relaxed">
            Tell us a bit about your business and we&apos;ll call within 60 minutes
            during working hours.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <div className="flex items-center gap-3"><Phone size={16} /> +91-99999-99999</div>
            <div className="flex items-center gap-3"><Mail size={16} /> hello@gst24mumbai.in</div>
          </div>
        </div>
        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-white rounded-2xl p-6 md:p-8 text-[#022C22] space-y-4"
          data-testid="lead-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold">Name</label>
              <div className="mt-1 relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  data-testid="lead-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white border border-[#D1D5DB] rounded-lg pl-9 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#064E3B]"
                  placeholder="Your full name"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold">Phone</label>
              <div className="mt-1 relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  data-testid="lead-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-white border border-[#D1D5DB] rounded-lg pl-9 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#064E3B]"
                  placeholder="+91 9xxxx xxxxx"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold">Email</label>
            <div className="mt-1 relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                data-testid="lead-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-white border border-[#D1D5DB] rounded-lg pl-9 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#064E3B]"
                placeholder="you@business.com"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold">Service interested in</label>
            <div className="mt-1">
              <Select
                value={form.service_interested}
                onValueChange={(v) => setForm({ ...form, service_interested: v })}
              >
                <SelectTrigger data-testid="lead-service" className="bg-white border-[#D1D5DB]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold">Message (optional)</label>
            <textarea
              data-testid="lead-message"
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full bg-white border border-[#D1D5DB] rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#064E3B]"
              placeholder="Tell us a bit about your business…"
            />
          </div>
          <button
            type="submit"
            data-testid="lead-submit"
            disabled={loading}
            className="btn-primary w-full justify-center disabled:opacity-60"
          >
            {loading ? "Sending…" : (<><Zap size={18} /> Get free consultation</>)}
          </button>
        </form>
      </div>
    </section>
  );
}

function Faq() {
  const items = [
    {
      q: "How long does GST registration really take?",
      a: "With a complete document set, we file the same day. GSTIN is typically issued by the GSTN portal in 24 working hours, sometimes faster.",
    },
    {
      q: "What is the difference between VPOB and APOB?",
      a: "VPOB (Virtual Place of Business) is your primary business address in Maharashtra if you don't have one. APOB (Additional Place of Business) is added to an existing GSTIN as an extra location.",
    },
    {
      q: "Is the Mumbai address valid for Amazon & Flipkart?",
      a: "Yes. We provide a real commercial address with NOC, rent agreement, utility bill and signage — fully compliant for marketplace verification.",
    },
    {
      q: "What documents do I need?",
      a: "PAN, Aadhaar, a passport-size photo, and business proof (bank statement or cancelled cheque). We send a checklist after you submit the form.",
    },
    {
      q: "Are government fees included in the price?",
      a: "GST registration itself is free of government fees. Our plan covers professional filing, document drafting and 24-hour follow-up.",
    },
  ];
  return (
    <section id="faq" className="py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9] text-center">
          FAQ
        </div>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#022C22] mt-3 tracking-tight text-center">
          Questions, answered.
        </h2>
        <div className="mt-12">
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {items.map((it, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white border border-[#E5E7EB] rounded-xl px-5"
              >
                <AccordionTrigger className="font-display font-semibold text-[#022C22] text-left hover:no-underline">
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3F6255] leading-relaxed">
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#022C22] text-[#A7F3D0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-white text-[#064E3B] flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <span className="font-display text-xl font-bold text-white">GST24 Mumbai</span>
          </div>
          <p className="mt-3 text-sm max-w-sm text-[#D1FAE5]">
            GST registration, VPOB &amp; APOB done in 24 hours for Indian e-commerce sellers expanding into Maharashtra.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white font-bold">Services</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a href="#pricing" className="hover:text-white">24h GST Registration</a></li>
            <li><a href="#pricing" className="hover:text-white">VPOB Mumbai</a></li>
            <li><a href="#pricing" className="hover:text-white">APOB Mumbai</a></li>
            <li><a href="#pricing" className="hover:text-white">Bundle</a></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white font-bold">Company</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a href="#how" className="hover:text-white">How it works</a></li>
            <li><a href="#faq" className="hover:text-white">FAQ</a></li>
            <li><Link to="/login" data-testid="footer-admin-link" className="hover:text-white">Admin login</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5 text-xs text-[#A7F3D0]/80 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} GST24 Mumbai. All rights reserved.</span>
          <span>Mumbai, Maharashtra, India</span>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppButton() {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-fab"
      className="fixed bottom-6 right-6 z-40 bg-[#10B981] hover:bg-[#059669] text-white rounded-full p-4 shadow-xl shadow-[#10B981]/30 transition-all hover:scale-105"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle size={24} />
    </a>
  );
}

export default function LandingPage() {
  const [plans, setPlans] = useState([]);
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/plans");
        setPlans(res.data);
      } catch (e) {
        console.error("Could not load plans", e);
      }
    })();
  }, []);

  const onBuy = async (planId) => {
    setBuyingId(planId);
    try {
      const res = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout. Try again.");
        setBuyingId(null);
      }
    } catch (e) {
      console.error(e);
      toast.error("Checkout failed. Please try again.");
      setBuyingId(null);
    }
  };

  return (
    <div>
      <Header />
      <main>
        <Hero />
        <TrustBadges />
        <HowItWorks />
        <Pricing plans={plans} onBuy={onBuy} buyingId={buyingId} />
        <VpobExplainer />
        <LeadForm plans={plans} />
        <Faq />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
